import { useEffect, useState, useRef } from 'react';
import posthog from 'posthog-js';
import { analytics } from '../utils/analytics';
import logger from '../utils/logger';
// Cache to track which flags have been ensured this session
const ensuredFlags = new Set<string>();

/**
 * Ensure feature flag exists in PostHog before checking it
 * This calls our server-side API which creates the flag if it doesn't exist
 */
async function ensureFeatureFlagExists(
    flagKey: string,
    defaultEnabled: boolean = false
): Promise<boolean> {
    // Skip if already ensured this session
    if (ensuredFlags.has(flagKey)) {
        return true;
    }

    try {
        const response = await fetch('/api/feature-flags/ensure', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                flagKey,
                // Use the defaultEnabled value passed from the hook caller
                // This ensures new flags are created with the intended default state
                defaultEnabled,
            }),
        });

        if (response.ok) {
            const result = await response.json();
            if (result.success) {
                ensuredFlags.add(flagKey);
                if (result.created) {
                    logger.debug(
                        `[FeatureFlag] Created flag in PostHog: ${flagKey} (disabled by default)`
                    );
                }
                return true;
            }
        }

        console.warn(
            `[FeatureFlag] Failed to ensure flag ${flagKey}:`,
            await response.text()
        );
        return false;
    } catch (error) {
        // Don't block on network errors - flag might already exist
        console.warn(
            `[FeatureFlag] Network error ensuring flag ${flagKey}:`,
            error
        );
        return false;
    }
}

/**
 * Hook to check if a feature flag is enabled for the current user
 * Automatically creates the flag in PostHog if it doesn't exist (disabled by default)
 * Tracks feature flag usage for analytics
 *
 * @param flagKey - The key of the feature flag as defined in PostHog
 * @param defaultValue - Default value to use while loading or if flag doesn't exist
 * @returns boolean indicating if the feature is enabled
 *
 * @example
 * const showBetaFeature = useFeatureFlag('beta_genre_explorer', false);
 */
export function useFeatureFlag(
    flagKey: string,
    defaultValue: boolean = false
): boolean {
    const [isEnabled, setIsEnabled] = useState<boolean>(defaultValue);

    // Use refs to ensure operations only happen once
    const hasEnsuredRef = useRef<boolean>(false);
    const hasTrackedRef = useRef<boolean>(false);

    useEffect(() => {
        if (typeof window === 'undefined' || !posthog) {
            return;
        }

        const initializeFlag = async () => {
            try {
                // Step 1: Ensure flag exists in PostHog (only once)
                if (!hasEnsuredRef.current) {
                    hasEnsuredRef.current = true;
                    // Pass defaultValue so new flags are created with the correct initial state
                    const ensured = await ensureFeatureFlagExists(
                        flagKey,
                        defaultValue
                    );

                    // If ensure failed (API not configured), use default value and don't check PostHog
                    if (!ensured) {
                        logger.debug(
                            `[FeatureFlag] Using default value for ${flagKey}:`,
                            defaultValue
                        );
                        setIsEnabled(defaultValue);
                        // Mark as tracked to prevent further changes
                        hasTrackedRef.current = true;
                        return;
                    }
                }

                // Step 2: Check flag value after a moment for flags to load
                // Only do this if we haven't already set the default (ensured was true)
                if (hasTrackedRef.current) {
                    return; // Already handled by ensure failure
                }

                setTimeout(() => {
                    const flagValue = posthog.isFeatureEnabled(flagKey);
                    // Debug: Log all feature flags from PostHog
                    const allFlags =
                        posthog.featureFlags?.getFlagVariants?.() || {};
                    logger.debug(
                        '[FeatureFlag] ============ DEBUG START ============'
                    );
                    logger.debug(
                        '[FeatureFlag] All PostHog feature flags:',
                        allFlags
                    );
                    logger.debug(
                        '[FeatureFlag] PostHog distinctId:',
                        posthog.get_distinct_id?.()
                    );
                    logger.debug(
                        '[FeatureFlag] PostHog properties:',
                        posthog.get_property?.('$stored_person_properties')
                    );
                    logger.debug(`[FeatureFlag] Checking flag: '${flagKey}'`);
                    logger.debug(
                        `[FeatureFlag] isFeatureEnabled('${flagKey}') returned:`,
                        flagValue,
                        '(type:',
                        typeof flagValue,
                        ')'
                    );
                    logger.debug(
                        `[FeatureFlag] Raw flag value from getFlagVariants:`,
                        allFlags[flagKey]
                    );
                    logger.debug(
                        '[FeatureFlag] ============ DEBUG END ============'
                    );

                    // If PostHog returns undefined/null, use the default value
                    // Only treat explicit true as enabled, explicit false as disabled
                    const isActive =
                        flagValue === true
                            ? true
                            : flagValue === false
                            ? false
                            : defaultValue;
                    logger.debug(
                        `[FeatureFlag] Final value for '${flagKey}':`,
                        isActive,
                        `(default was: ${defaultValue})`
                    );
                    setIsEnabled(isActive);

                    // Track feature flag usage (only once)
                    if (!hasTrackedRef.current) {
                        hasTrackedRef.current = true;
                        analytics.capture('feature_flag_evaluated', {
                            feature_flag: flagKey,
                            flag_response: isActive,
                            flag_value: isActive ? 'enabled' : 'disabled',
                        });
                    }
                }, 100);
            } catch (error) {
                console.warn(
                    `[FeatureFlag] Error checking flag ${flagKey}:`,
                    error
                );
                setIsEnabled(defaultValue);
            }
        };

        initializeFlag();

        // For real-time updates, poll periodically WITHOUT tracking
        // Only poll if the ensure API succeeded (flags are working)
        const interval = setInterval(() => {
            try {
                const flagValue = posthog.isFeatureEnabled(flagKey);
                // If PostHog returns undefined/null, keep current value (don't override with default)
                if (flagValue === true || flagValue === false) {
                    setIsEnabled(flagValue);
                }
            } catch (error) {
                console.warn(
                    `[FeatureFlag] Error polling flag ${flagKey}:`,
                    error
                );
            }
        }, 30000); // Check every 30 seconds

        return () => {
            clearInterval(interval);
        };
    }, [flagKey, defaultValue]);

    return isEnabled;
}

/**
 * Hook to check if feature flags are still loading
 * Useful for showing loading states
 */
export function useFeatureFlagLoading(): boolean {
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        // PostHog loads feature flags automatically after initialization
        // We'll consider them loaded after a short delay
        const timeout = setTimeout(() => {
            setIsLoading(false);
        }, 1000);

        return () => {
            clearTimeout(timeout);
        };
    }, []);

    return isLoading;
}

/**
 * Get the payload/configuration for a feature flag
 * Useful for flags that return JSON configuration instead of just boolean
 *
 * @param flagKey - The key of the feature flag
 * @returns The flag payload or null
 */
export function useFeatureFlagPayload<T = any>(flagKey: string): T | null {
    const [payload, setPayload] = useState<T | null>(null);

    useEffect(() => {
        const checkPayload = () => {
            if (typeof window !== 'undefined' && posthog) {
                try {
                    const flagPayload = posthog.getFeatureFlagPayload(flagKey);
                    setPayload(flagPayload as T);
                } catch (error) {
                    console.warn(
                        `[FeatureFlag] Error getting payload for ${flagKey}:`,
                        error
                    );
                    setPayload(null);
                }
            }
        };

        // Check immediately
        checkPayload();

        // Poll for updates periodically
        const interval = setInterval(() => {
            checkPayload();
        }, 30000); // Check every 30 seconds

        return () => {
            clearInterval(interval);
        };
    }, [flagKey]);

    return payload;
}
